import React, { useState, useEffect, useRef } from "react";
import {
  FlatList,
  Image,
  Pressable,
  StyleSheet,
  Text,
  View,
  TouchableOpacity,
  Platform,
} from "react-native";
import {
  white,
  color_999999,
  ColorF5F5F5,
} from "../../assets/style/colors";
import { useWindowDimensions } from 'react-native';
import { FileResponse } from "../../utils/upload";
import * as ImSdk from "../../../imsdk/api";
import { Emoticon } from "../../../imsdk/db/t_emoticon";
import { MessageType } from "../../constants/types";
import globalEventBus from "../../utils/EventBus";

export type CollectPickerModalProps = {
  visible: boolean;
  onClose: () => void;
  onPicture: (res: FileResponse) => void;
  iconPosition?: { x: number; y: number; width: number; height: number };
};

export const CollectPickerModal = ({ visible, onClose, onPicture, iconPosition }: CollectPickerModalProps) => {
  const [data, setData] = useState<Emoticon[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 20;

  useEffect(() => {
    if (visible) {
      fetchData();
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    
    const handleDeleteEmoticon = (ids: string[]) => {
      setData((prevData) => {
        const newList = prevData.filter((v) => !ids.includes(v.id));
        return newList;
      });
    };

    const handleAddEmoticon = (emoticon: Emoticon) => {
      setData((prevState) => [
        emoticon,
        ...prevState,
      ]);
    };
    
    globalEventBus.on("DeleteEmoticon", handleDeleteEmoticon);
    globalEventBus.on("AddEmoticon", handleAddEmoticon);
    
    return () => {
      globalEventBus.off("DeleteEmoticon", handleDeleteEmoticon);
      globalEventBus.off("AddEmoticon", handleAddEmoticon);
    };
  }, [visible]);


  function fetchData() {
    setLoading(true);
    ImSdk.getInstance()
      .GetEmoticonList(data.length, pageSize)
      .then((r) => {
        if (r.emoticons && r.emoticons.length > 0) {
          const result = r.emoticons;
          setHasMore(pageSize === result.length);
          setData((prevState) => [...prevState, ...result]);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function loadMore() {
    if (data.length === 0 || !hasMore || loading) {
      return;
    }
    fetchData();
  }

  async function onClick(item: Emoticon) {
    console.log('[CollectPickerModal] onClick called', item);
    
    let width = 0;
    let height = 0;
    const url = item.url;
    const parseQueryParams = (urlString: string): { width?: string; height?: string } => {
      const params: { width?: string; height?: string } = {};
      const queryIndex = urlString.indexOf('?');
      if (queryIndex === -1) return params;
      
      const queryString = urlString.substring(queryIndex + 1);
      const pairs = queryString.split('&');
      
      for (const pair of pairs) {
        const equalIndex = pair.indexOf('=');
        if (equalIndex > 0) {
          const key = pair.substring(0, equalIndex);
          const value = pair.substring(equalIndex + 1);
          if (key === 'width' || key === 'height') {
            params[key] = decodeURIComponent(value);
          }
        }
      }
      return params;
    };

    const params = parseQueryParams(url);
    if (params.width && params.height) {
      width = parseInt(params.width, 10);
      height = parseInt(params.height, 10);
    }

    const result: FileResponse = {
      url: item.url,
      type: MessageType.IMAGE,
      width: width,
      height: height,
      mimeType: 'image/gif',
      localPath: item.url, // Web 平台使用 URL 作为 localPath
    };
    
    console.log('[CollectPickerModal] FileResponse:', result);
    console.log('[CollectPickerModal] onPicture exists:', !!onPicture);
    
    if (onPicture) {
      onPicture(result);
    } else {
      console.error('[CollectPickerModal] onPicture is not defined!');
    }
  }

  // 将 renderItem 改为独立组件，以便使用 hooks（参考 EmojiPickerModal）
  const CollectItem = React.memo(({ item, index }: { item: Emoticon; index: number }) => {
    console.log('[CollectPickerModal] CollectItem render', { index, itemId: item.id });
    
    const itemRef = useRef<View>(null);
    const [isPressed, setIsPressed] = useState(false);
    
    // 使用固定宽度，和表情选择器类似的布局
    const bubbleWidth = 320;
    const itemWidth = (bubbleWidth - 12 * 2 - 6 * 5) / 5.0; // 气泡宽度减去padding和margin
    
    const handleClick = () => {
      console.log('[CollectItem] handleClick called', item);
      onClick(item);
    };
    
    // 为每个 item 添加原生 DOM 事件监听器（参考 EmojiPickerModal）
    useEffect(() => {
      const view = itemRef.current;
      if (!view) {
        console.log('[CollectItem] itemRef.current 为空, itemId:', item.id);
        return;
      }

      const handleClick = (e: any) => {
        console.log('[CollectItem] DOM click 事件, itemId:', item.id, 'target:', e.target);
        e.stopPropagation();
        e.preventDefault();
        onClick(item);
      };

      // 延迟获取 DOM 节点，确保已经渲染
      const timeoutId = setTimeout(() => {
        try {
          const viewEl = view as any;
          let domNode: any = null;

          if (viewEl._internalFiberInstanceHandleDEV?.stateNode?.node) {
            domNode = viewEl._internalFiberInstanceHandleDEV.stateNode.node;
            console.log('[CollectItem] 通过方法1找到DOM节点, itemId:', item.id);
          } else if (viewEl._nativeNode) {
            domNode = viewEl._nativeNode;
            console.log('[CollectItem] 通过方法2找到DOM节点, itemId:', item.id);
          } else if (viewEl.nodeName) {
            // 如果已经是 DOM 元素
            domNode = viewEl;
            console.log('[CollectItem] 通过方法3找到DOM节点, itemId:', item.id);
          }

          if (domNode) {
            console.log('[CollectItem] 成功找到DOM节点，添加事件监听器, itemId:', item.id);
            domNode.addEventListener('click', handleClick, true);
            domNode.addEventListener('mousedown', handleClick, true);
          } else {
            console.log('[CollectItem] 未找到DOM节点, itemId:', item.id, 'viewEl:', viewEl);
          }
        } catch (e) {
          console.log('[CollectItem] 添加事件监听器失败, itemId:', item.id, '错误:', e);
        }
      }, 100);

      return () => {
        clearTimeout(timeoutId);
      };
    }, [item, onClick]);

    return (
      <View
        ref={itemRef}
        style={{
          width: itemWidth,
          height: itemWidth,
          margin: 3,
          borderRadius: 8,
          overflow: 'hidden',
          backgroundColor: ColorF5F5F5,
          ...(Platform.OS === 'web' && {
            cursor: 'pointer',
          } as any),
        }}
        onStartShouldSetResponder={() => {
          console.log('[CollectItem] onStartShouldSetResponder, itemId:', item.id);
          return true;
        }}
        onResponderGrant={() => {
          console.log('[CollectItem] onResponderGrant, itemId:', item.id);
          setIsPressed(true);
          handleClick();
          // 延迟重置按下状态，提供视觉反馈
          setTimeout(() => setIsPressed(false), 200);
        }}
      >
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={() => {
            console.log('[CollectItem] TouchableOpacity onPress 被调用, itemId:', item.id);
            setIsPressed(true);
            handleClick();
            setTimeout(() => setIsPressed(false), 200);
          }}
          onPressIn={() => setIsPressed(true)}
          onPressOut={() => setTimeout(() => setIsPressed(false), 150)}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 8,
            backgroundColor: isPressed ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
          }}
        >
          <Image
            source={{ uri: item.url }}
            style={{
              width: '100%',
              height: '100%',
            }}
            resizeMode="cover"
          />
        </TouchableOpacity>
      </View>
    );
  });

  function renderItem({ item, index }: { item: Emoticon; index: number }) {
    return <CollectItem item={item} index={index} />;
  }

  if (!visible) {
    return null;
  }

  // 计算气泡位置，靠左显示（固定位置，避免闪烁）
  const bubbleWidth = 320; // 和表情选择器一样的宽度
  const inputAreaHeight = 200; // 输入区域高度
  const spaceFromInput = 8; // 距离输入框的间距
  
  const bubbleStyle = {
    width: bubbleWidth,
    bottom: inputAreaHeight + spaceFromInput, // 在输入框上方
    left: 16, // 靠左显示
  };

  console.log('[CollectPickerModal] Rendering modal, visible:', visible, 'data length:', data.length);
  
  // 暂时移除遮罩层，避免拦截点击事件（参考 EmojiPickerModal）
  // 点击外部关闭的功能由 ChatInput 中的 handleClickOutside 处理
  
  return (
    <>
      {/* 气泡 */}
      <View 
        style={[styles.bubble, bubbleStyle]}
        data-collect-picker="true"
        onStartShouldSetResponder={() => {
          console.log('[CollectPickerModal] 气泡 onStartShouldSetResponder');
          return true;
        }}
        onMoveShouldSetResponder={() => {
          console.log('[CollectPickerModal] 气泡 onMoveShouldSetResponder');
          return true;
        }}
        onResponderGrant={() => {
          console.log('[CollectPickerModal] 气泡 onResponderGrant');
        }}
      >
        <View 
          style={styles.content} 
          data-collect-picker-scroll="true" 
          pointerEvents="box-none"
        >
          <FlatList
            data={data}
            renderItem={renderItem}
            numColumns={5}
            onEndReached={loadMore}
            onEndReachedThreshold={0.2}
            keyExtractor={(item, index) => `${index}_${item.id}_${item.ctime}`}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={true}
            removeClippedSubviews={false} // Web 上禁用，避免渲染问题
            ListEmptyComponent={
              <View style={styles.emptyContainer}>
                <Text style={{ color: color_999999, fontSize: 14, marginTop: 12 }}>暂无收藏表情</Text>
              </View>
            }
          />
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    zIndex: 999,
  },
  bubble: {
    position: 'absolute',
    backgroundColor: white,
    borderRadius: 16,
    maxHeight: 380,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
    zIndex: 1000,
    overflow: 'hidden',
  },
  content: {
    minHeight: 220,
    maxHeight: 320,
    padding: 12,
  },
  listContent: {
    paddingBottom: 12,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
});
